import React, { useEffect, useMemo, useState } from "react";
import { EditOutlined, ProfileOutlined } from "@ant-design/icons";
import {
  Alert,
  Badge,
  Button,
  Drawer,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { PermissionKey } from "@fitness-app/data-models";
import { type Database } from "@fitness-app/supabase";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import { usePermission } from "~/hooks/usePermission";
import { supabase } from "~/store/initializeStore";

type BlacklistRow = Database["public"]["Tables"]["sales_booking_blacklist"]["Row"];
type AttemptRow = Database["public"]["Tables"]["sales_booking_attempt"]["Row"];

export type BlacklistPrefill = {
  email?: string | null;
  phone?: string | null;
  visitorId?: string | null;
  ip?: string | null;
  reason?: string | null;
};

type DeviceBlock = { visitorId: string; reason: string };

type BlacklistForm = {
  email?: string;
  phone?: string;
  visitorId?: string;
  ip?: string;
  mode: BlacklistRow["mode"];
  reason?: string;
};

type SalesBlacklistProps = {
  prefill?: BlacklistPrefill | null;
  onPrefillConsumed?: () => void;
};

const BLACKLIST_QUERY_KEY = ["salesCrmBlacklist"];
const GMAIL_DOMAINS = new Set(["gmail.com", "googlemail.com"]);

// Keep in sync with apps/ecommerce src/pages/api/calendly/book.ts `normalizeEmail`.
const normalizeBlacklistEmail = (email: string): string | null => {
  const trimmed = email.trim().toLowerCase();
  if (!trimmed) return null;
  const atIndex = trimmed.lastIndexOf("@");
  if (atIndex <= 0) return trimmed;
  const localPart = trimmed.slice(0, atIndex);
  const domain = trimmed.slice(atIndex + 1);
  if (!GMAIL_DOMAINS.has(domain)) return trimmed;
  const withoutPlus = localPart.split("+")[0] ?? localPart;
  const withoutDots = withoutPlus.replace(/\./g, "");
  return `${withoutDots}@${domain}`;
};

// Mirror the booking handler's phone normalisation: "+<country><digits>", PL (+48) default.
const normalizeBlacklistPhone = (phone: string): string | null => {
  let digits = phone.replace(/[^\d+]/g, "");
  if (!digits) return null;
  if (digits.startsWith("00")) digits = `+${digits.slice(2)}`;
  if (digits.startsWith("+")) return digits;
  return `+48${digits.replace(/^0+/, "")}`;
};

const modeTag = (mode: BlacklistRow["mode"]) =>
  mode === "reject" ? <Tag color="red">Widoczna</Tag> : <Tag color="blue">Cicha</Tag>;

const outcomeLabels: Record<AttemptRow["outcome"], { label: string; color: string }> = {
  allowed: { label: "Przeszła", color: "green" },
  blocked: { label: "Zablokowana", color: "red" },
  error: { label: "Błąd", color: "orange" },
};

const quoteInValue = (value: string) => `"${value.replace(/"/g, '\\"')}"`;

const SalesBlacklist = ({ prefill, onPrefillConsumed }: SalesBlacklistProps): React.ReactElement => {
  const queryClient = useQueryClient();
  const { userId } = useUserRole();
  // Lustro polityk RLS: czytanie blacklisty wisi na sales.read ALL, a zapisy na
  // sales.manage (dowolny zakres — sprzedawca ma ASSIGNED). Zakładka była
  // widoczna po samym odczycie, a formularz i akcje renderowały się
  // bezwarunkowo, więc konto bez prawa zapisu wypełniało modal i dostawało
  // „new row violates row-level security policy" dopiero przy zapisie.
  const canManageBlacklist = usePermission(PermissionKey.SALES_MANAGE);
  const [form] = Form.useForm<BlacklistForm>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [aliasEntry, setAliasEntry] = useState<BlacklistRow | null>(null);
  const watchedVisitorId = Form.useWatch("visitorId", form);
  const watchedIp = Form.useWatch("ip", form);

  const blacklistQuery = useQuery({
    queryKey: BLACKLIST_QUERY_KEY,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sales_booking_blacklist")
        .select("*")
        .order("createdAt", { ascending: false })
        .limit(500);

      if (error) throw new Error(error.message);
      return (data ?? []) as BlacklistRow[];
    },
  });

  const openCreateModal = (initial?: BlacklistPrefill) => {
    form.resetFields();
    setEditingId(null);
    form.setFieldsValue({
      mode: "silent",
      email: initial?.email ?? undefined,
      phone: initial?.phone ?? undefined,
      visitorId: initial?.visitorId ?? undefined,
      ip: initial?.ip ?? undefined,
      reason: initial?.reason ?? undefined,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (row: BlacklistRow) => {
    form.resetFields();
    setEditingId(row.id);
    form.setFieldsValue({
      mode: row.mode,
      email: row.email ?? undefined,
      phone: row.phone ?? undefined,
      visitorId: row.visitorId ?? undefined,
      ip: row.ip ?? undefined,
      reason: row.reason ?? undefined,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    form.resetFields();
  };

  useEffect(() => {
    if (prefill) {
      openCreateModal(prefill);
      onPrefillConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const saveMutation = useMutation({
    mutationFn: async (values: BlacklistForm) => {
      const email = values.email ? normalizeBlacklistEmail(values.email) : null;
      const phone = values.phone ? normalizeBlacklistPhone(values.phone) : null;
      const visitorId = values.visitorId?.trim() || null;
      const ip = values.ip?.trim() || null;

      if (!email && !phone && !visitorId && !ip) {
        throw new Error("Podaj przynajmniej jedną daną — e-mail, telefon, urządzenie lub IP.");
      }

      if (editingId) {
        const { error } = await supabase
          .from("sales_booking_blacklist")
          .update({
            email,
            phone,
            visitorId,
            ip,
            mode: values.mode,
            reason: values.reason?.trim() || null,
            updatedAt: new Date().toISOString(),
          })
          .eq("id", editingId);
        if (error) throw new Error(error.message);
        return;
      }

      const { error } = await supabase.from("sales_booking_blacklist").insert({
        email,
        phone,
        visitorId,
        ip,
        mode: values.mode,
        reason: values.reason?.trim() || null,
        createdBy: userId || null,
      });

      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      const wasEditing = Boolean(editingId);
      closeModal();
      await queryClient.invalidateQueries({ queryKey: BLACKLIST_QUERY_KEY });
      void message.success(wasEditing ? "Zapisano zmiany" : "Dodano osobę do listy");
    },
    onError: (error: Error) => {
      void message.error(error.message);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async (row: BlacklistRow) => {
      const { error } = await supabase
        .from("sales_booking_blacklist")
        .update({ active: !row.active, updatedAt: new Date().toISOString() })
        .eq("id", row.id);

      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: BLACKLIST_QUERY_KEY });
      void message.success("Zaktualizowano wpis");
    },
    onError: (error: Error) => {
      void message.error(error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("sales_booking_blacklist").delete().eq("id", id);
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: BLACKLIST_QUERY_KEY });
      void message.success("Usunięto wpis");
    },
    onError: (error: Error) => {
      void message.error(error.message);
    },
  });

  const blockDevicesMutation = useMutation({
    mutationFn: async (devices: DeviceBlock[]) => {
      if (devices.length === 0) return;
      const rows = devices.map((device) => ({
        visitorId: device.visitorId,
        mode: "silent" as const,
        reason: device.reason,
        createdBy: userId || null,
      }));
      const { error } = await supabase.from("sales_booking_blacklist").insert(rows);
      if (error) throw new Error(error.message);
    },
    onSuccess: async (_data, devices) => {
      setAliasEntry(null);
      await queryClient.invalidateQueries({ queryKey: BLACKLIST_QUERY_KEY });
      void message.success(`Zablokowano urządzenia: ${devices.length}`);
    },
    onError: (error: Error) => {
      void message.error(error.message);
    },
  });

  const rows = blacklistQuery.data ?? [];

  return (
    <Space direction="vertical" style={{ width: "100%" }} size={16}>
      <Alert
        showIcon
        type="info"
        message="Kogo blokujemy?"
        description="Osoby z tej listy nie zarezerwują konsultacji. Dodajesz je po adresie e-mail, numerze telefonu lub urządzeniu. Jeśli ktoś spróbuje zapisać się ponownie z innego e-maila, ale z tego samego telefonu albo przeglądarki — i tak go zatrzymamy. Przy każdym wpisie wybierasz, czy blokada ma być cicha (osoba myśli, że się zapisała, ale termin nie zostaje zajęty), czy widoczna (zobaczy komunikat, że nie może się zapisać)."
      />

      <div className="flex items-center justify-between">
        <Typography.Text type="secondary">
          Zablokowanych osób: {rows.filter((row) => row.active).length}
        </Typography.Text>
        {canManageBlacklist ? (
          <Button type="primary" onClick={() => openCreateModal()}>
            Zablokuj osobę
          </Button>
        ) : null}
      </div>

      {blacklistQuery.isError ? (
        <Alert
          showIcon
          type="warning"
          message="Nie udało się wczytać listy"
          description={(blacklistQuery.error as Error).message}
        />
      ) : null}

      <Table<BlacklistRow>
        rowKey="id"
        dataSource={rows}
        loading={blacklistQuery.isLoading}
        scroll={{ x: true }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      >
        <Table.Column<BlacklistRow>
          title="Dane osoby"
          render={(_, row) => (
            <Space direction="vertical" size={0}>
              {row.email ? <Typography.Text>{row.email}</Typography.Text> : null}
              {row.phone ? <Typography.Text type="secondary">{row.phone}</Typography.Text> : null}
              {row.visitorId ? (
                <Typography.Text type="secondary" className="text-xs">
                  urządzenie: {row.visitorId}
                </Typography.Text>
              ) : null}
              {row.ip ? (
                <Typography.Text type="secondary" className="text-xs">
                  IP: {row.ip}
                </Typography.Text>
              ) : null}
            </Space>
          )}
        />
        <Table.Column<BlacklistRow>
          title="Rodzaj blokady"
          dataIndex="mode"
          width={140}
          render={(value) => modeTag(value)}
        />
        <Table.Column<BlacklistRow>
          title="Powód"
          dataIndex="reason"
          render={(value) => value || <Typography.Text type="secondary">-</Typography.Text>}
        />
        <Table.Column<BlacklistRow>
          title="Zablokowane próby"
          width={150}
          render={(_, row) => (
            <Space direction="vertical" size={0}>
              <Typography.Text>{row.hitCount}</Typography.Text>
              {row.lastHitAt ? (
                <Typography.Text type="secondary" className="text-xs">
                  {dayjs(row.lastHitAt).format("DD.MM.YYYY HH:mm")}
                </Typography.Text>
              ) : null}
            </Space>
          )}
        />
        <Table.Column<BlacklistRow>
          title="Status"
          dataIndex="active"
          width={120}
          render={(value: boolean) =>
            value ? <Badge status="error" text="Aktywny" /> : <Badge status="default" text="Wyłączony" />
          }
        />
        <Table.Column<BlacklistRow>
          title="Dodano"
          dataIndex="createdAt"
          width={140}
          render={(value) => dayjs(value).format("DD.MM.YYYY")}
        />
        <Table.Column<BlacklistRow>
          title=""
          width={260}
          render={(_, row) => (
            <Space>
              <Button size="small" icon={<ProfileOutlined />} onClick={() => setAliasEntry(row)}>
                Sprawdź aliasy
              </Button>
              {canManageBlacklist ? (
                <>
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(row)}>
                    Edytuj
                  </Button>
                  <Button size="small" loading={toggleMutation.isLoading} onClick={() => toggleMutation.mutate(row)}>
                    {row.active ? "Wyłącz" : "Włącz"}
                  </Button>
                  <Popconfirm
                    title="Usunąć z listy?"
                    okText="Usuń"
                    cancelText="Anuluj"
                    onConfirm={() => deleteMutation.mutate(row.id)}
                  >
                    <Button size="small" danger>
                      Usuń
                    </Button>
                  </Popconfirm>
                </>
              ) : null}
            </Space>
          )}
        />
      </Table>

      <Modal
        title={editingId ? "Edytuj blokadę" : "Zablokuj osobę"}
        open={isModalOpen}
        onCancel={closeModal}
        onOk={() => form.submit()}
        confirmLoading={saveMutation.isLoading}
        okText={editingId ? "Zapisz" : "Zablokuj"}
        cancelText="Anuluj"
        width={560}
      >
        <Form form={form} layout="vertical" onFinish={(values) => saveMutation.mutate(values)}>
          <Typography.Paragraph type="secondary" className="!mb-3">
            Wystarczy jedna dana — najczęściej e-mail lub telefon. Wielkość liter i kropki w adresie Gmail nie mają
            znaczenia, rozpoznamy tę samą osobę.
          </Typography.Paragraph>

          {watchedVisitorId || watchedIp ? (
            <Alert
              showIcon
              type="warning"
              className="!mb-3"
              message="Blokada obejmuje też urządzenie / sieć"
              description="Ta blokada zatrzyma tę osobę nawet jeśli spróbuje zapisać się z nowego e-maila lub telefonu z tej samej przeglądarki."
            />
          ) : null}

          <Form.Item name="email" label="E-mail">
            <Input placeholder="np. anna.kowalska@gmail.com" allowClear />
          </Form.Item>
          <Form.Item name="phone" label="Telefon">
            <Input placeholder="np. +48 123 456 789" allowClear />
          </Form.Item>
          {/* Urządzenie / IP nie są wpisywane ręcznie — ustawia je tylko akcja „Sprawdź aliasy”. */}
          <Form.Item name="visitorId" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="ip" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="mode" label="Rodzaj blokady" rules={[{ required: true }]}>
            <Select
              options={[
                {
                  value: "silent",
                  label: "Cicha — osoba myśli, że się zapisała, ale termin nie zostaje zajęty",
                },
                { value: "reject", label: "Widoczna — osoba zobaczy komunikat, że nie może się zapisać" },
              ]}
            />
          </Form.Item>
          <Form.Item name="reason" label="Powód (notatka dla zespołu)">
            <Input.TextArea rows={2} placeholder="np. trzy razy umówiła się i nie przyszła" />
          </Form.Item>
        </Form>
      </Modal>

      <AliasesDrawer
        entry={aliasEntry}
        activeRules={rows.filter((row) => row.active)}
        onClose={() => setAliasEntry(null)}
        onBlacklist={(prefillValues) => {
          setAliasEntry(null);
          openCreateModal(prefillValues);
        }}
        onBlockAllDevices={(devices) => blockDevicesMutation.mutate(devices)}
        blockingDevices={blockDevicesMutation.isLoading}
        canManageBlacklist={canManageBlacklist}
      />
    </Space>
  );
};

type AliasesDrawerProps = {
  entry: BlacklistRow | null;
  activeRules: BlacklistRow[];
  onClose: () => void;
  onBlacklist: (prefill: BlacklistPrefill) => void;
  onBlockAllDevices: (devices: DeviceBlock[]) => void;
  blockingDevices: boolean;
  /** Podgląd aliasów jest odczytem; blokowanie z tego miejsca to już zapis. */
  canManageBlacklist: boolean;
};

const AliasesDrawer = ({
  entry,
  activeRules,
  onClose,
  onBlacklist,
  onBlockAllDevices,
  blockingDevices,
  canManageBlacklist,
}: AliasesDrawerProps): React.ReactElement => {
  const aliasesQuery = useQuery({
    queryKey: ["salesCrmBlacklistAliases", entry?.id],
    enabled: Boolean(entry),
    retry: false,
    queryFn: async () => {
      if (!entry) return [];

      // Seed keys: whatever identifiers this blacklist entry already has.
      const seedClauses: string[] = [];
      if (entry.email) seedClauses.push(`email.eq.${quoteInValue(entry.email)}`);
      if (entry.phone) seedClauses.push(`phone.eq.${quoteInValue(entry.phone)}`);
      if (entry.visitorId) seedClauses.push(`visitorId.eq.${quoteInValue(entry.visitorId)}`);
      if (entry.ip) seedClauses.push(`ip.eq.${quoteInValue(entry.ip)}`);
      if (seedClauses.length === 0) return [];

      const visitorIds = new Set<string>();
      const ips = new Set<string>();
      if (entry.visitorId) visitorIds.add(entry.visitorId);
      if (entry.ip) ips.add(entry.ip);

      // Hop 1: attempts directly matching the entry → discover the devices/IPs that person used.
      const { data: seedAttempts, error: seedError } = await supabase
        .from("sales_booking_attempt")
        .select("visitorId, ip")
        .or(seedClauses.join(","))
        .limit(500);
      if (seedError) throw new Error(seedError.message);
      (seedAttempts ?? []).forEach((attempt) => {
        if (attempt.visitorId) visitorIds.add(attempt.visitorId);
        if (attempt.ip) ips.add(attempt.ip);
      });

      // Hop 2: every attempt that shares the seed keys OR any of those devices/IPs (alias graph).
      const orClauses = [...seedClauses];
      if (visitorIds.size > 0) orClauses.push(`visitorId.in.(${[...visitorIds].map(quoteInValue).join(",")})`);
      if (ips.size > 0) orClauses.push(`ip.in.(${[...ips].map(quoteInValue).join(",")})`);

      const { data, error } = await supabase
        .from("sales_booking_attempt")
        .select("*")
        .or(orClauses.join(","))
        .order("createdAt", { ascending: false })
        .limit(500);
      if (error) throw new Error(error.message);
      return (data ?? []) as AttemptRow[];
    },
  });

  const attempts = aliasesQuery.data ?? [];

  // What is already actively blocked — so we never suggest re-blocking it.
  const blockedEmails = useMemo(
    () => new Set(activeRules.map((rule) => rule.email).filter((value): value is string => Boolean(value))),
    [activeRules],
  );
  const blockedVisitorIds = useMemo(
    () => new Set(activeRules.map((rule) => rule.visitorId).filter((value): value is string => Boolean(value))),
    [activeRules],
  );

  const distinctEmails = useMemo(() => {
    const set = new Set<string>();
    attempts.forEach((attempt) => {
      if (attempt.email && !blockedEmails.has(attempt.email)) set.add(attempt.email);
    });
    return [...set];
  }, [attempts, blockedEmails]);
  // Per-device (visitorId) → which e-mails were seen on it, so each device block carries context.
  const deviceEmails = useMemo(() => {
    const map = new Map<string, Set<string>>();
    attempts.forEach((attempt) => {
      if (!attempt.visitorId) return;
      const emails = map.get(attempt.visitorId) ?? new Set<string>();
      if (attempt.email) emails.add(attempt.email);
      map.set(attempt.visitorId, emails);
    });
    return map;
  }, [attempts]);
  const distinctVisitorIds = useMemo(
    () => [...deviceEmails.keys()].filter((visitorId) => !blockedVisitorIds.has(visitorId)),
    [deviceEmails, blockedVisitorIds],
  );

  const reasonForDevice = (visitorId: string): string => {
    const emails = [...(deviceEmails.get(visitorId) ?? [])];
    return emails.length > 0
      ? `Urządzenie powiązane z: ${emails.join(", ")}`
      : "Urządzenie z prób rezerwacji (brak e-maila)";
  };

  return (
    <Drawer title="Możliwe aliasy tej osoby" width={720} open={Boolean(entry)} onClose={onClose}>
      <Space direction="vertical" style={{ width: "100%" }} size={16}>
        <Alert
          showIcon
          type="info"
          message="Skąd to wiemy?"
          description="Poniżej są próby rezerwacji z tego samego urządzenia lub sieci. Jeśli widzisz inne adresy e-mail albo numery telefonu — to prawdopodobnie ta sama osoba próbująca zapisać się ponownie. Kliknij „+ zablokuj”, żeby dodać ją do listy."
        />

        {attempts.length > 0 && distinctEmails.length === 0 && distinctVisitorIds.length === 0 ? (
          <Alert
            showIcon
            type="success"
            message="Wszystko już zablokowane"
            description="Wszystkie e-maile i urządzenia wykryte w tych próbach są już na liście. Nie ma nic nowego do dodania."
          />
        ) : null}

        {distinctEmails.length > 0 ? (
          <div>
            <Typography.Text strong>Inne adresy e-mail tej samej osoby:</Typography.Text>
            <div className="mt-2 flex flex-wrap gap-2">
              {distinctEmails.map((email) =>
                canManageBlacklist ? (
                  <Tag key={email} color="volcano" className="cursor-pointer" onClick={() => onBlacklist({ email })}>
                    {email} + zablokuj
                  </Tag>
                ) : (
                  <Tag key={email} color="volcano">
                    {email}
                  </Tag>
                ),
              )}
            </div>
          </div>
        ) : null}

        {distinctVisitorIds.length > 0 ? (
          <div>
            <div className="flex items-center justify-between gap-2">
              <Typography.Text strong>Urządzenia tej osoby:</Typography.Text>
              {canManageBlacklist && distinctVisitorIds.length > 1 ? (
                <Button
                  size="small"
                  danger
                  loading={blockingDevices}
                  onClick={() =>
                    onBlockAllDevices(
                      distinctVisitorIds.map((visitorId) => ({ visitorId, reason: reasonForDevice(visitorId) })),
                    )
                  }
                >
                  Zablokuj wszystkie urządzenia ({distinctVisitorIds.length})
                </Button>
              ) : null}
            </div>
            <div className="mt-2 flex flex-col gap-2">
              {distinctVisitorIds.map((visitorId, index) => {
                const emails = [...(deviceEmails.get(visitorId) ?? [])];
                return (
                  <div key={visitorId} className="flex items-center gap-2">
                    {canManageBlacklist ? (
                      <Tag
                        color="purple"
                        className="cursor-pointer"
                        onClick={() => onBlacklist({ visitorId, reason: reasonForDevice(visitorId) })}
                      >
                        Zablokuj urządzenie #{index + 1}
                      </Tag>
                    ) : (
                      <Tag color="purple">Urządzenie #{index + 1}</Tag>
                    )}
                    <Typography.Text type="secondary" className="text-xs">
                      {emails.length > 0 ? emails.join(", ") : "brak e-maila w próbach"}
                    </Typography.Text>
                  </div>
                );
              })}
            </div>
            <Typography.Text type="secondary" className="mt-2 block text-xs">
              Blokada urządzenia zatrzyma też przyszłe próby z tej samej przeglądarki — nawet z nowym e-mailem.
            </Typography.Text>
          </div>
        ) : null}

        <Table<AttemptRow>
          rowKey="id"
          size="small"
          dataSource={attempts}
          loading={aliasesQuery.isLoading}
          scroll={{ x: true }}
          pagination={{ pageSize: 15 }}
        >
          <Table.Column<AttemptRow>
            title="Kiedy"
            dataIndex="createdAt"
            render={(value) => dayjs(value).format("DD.MM HH:mm")}
          />
          <Table.Column<AttemptRow> title="E-mail" dataIndex="email" render={(value) => value ?? "-"} />
          <Table.Column<AttemptRow> title="Telefon" dataIndex="phone" render={(value) => value ?? "-"} />
          <Table.Column<AttemptRow> title="Imię" dataIndex="firstName" render={(value) => value ?? "-"} />
          <Table.Column<AttemptRow> title="IP" dataIndex="ip" render={(value) => value ?? "-"} />
          <Table.Column<AttemptRow>
            title="Co się stało"
            dataIndex="outcome"
            render={(value: AttemptRow["outcome"]) => {
              const meta = outcomeLabels[value] ?? { label: value, color: "default" };
              return <Tag color={meta.color}>{meta.label}</Tag>;
            }}
          />
        </Table>
      </Space>
    </Drawer>
  );
};

export default SalesBlacklist;
